/**
 * PagePreview
 *
 * Admin preview route: /preview/:slug
 *
 * Loads a page template by slug (including drafts) and renders it
 * using the block-based PageRenderer. Shows a "Preview Mode" banner
 * for draft pages so it's clear this is not a live page.
 *
 * This route is intended for admin use — it bypasses the published
 * status check that the public /p/:slug route enforces.
 */
import React, { useState, useEffect, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Eye, ArrowLeft, ExternalLink, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import { PageRenderer } from '../components/new-components/PageBuilder';
import SiteTemplateRenderer from '../components/new-components/PageBuilder/SiteTemplateRenderer';
import type { PageConfig } from '../components/new-components/PageBuilder/types';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */
interface PageData {
  id: string;
  title: string;
  slug: string;
  entity_type?: string;
  entity_id?: string | null;
  status: 'draft' | 'published';
  section_content: PageConfig & {
    settings?: {
      siteTemplate?: string;
      sections?: Record<string, unknown>;
    };
  };
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
function unwrapPage(res: unknown): PageData | null {
  if (!res) return null;
  const d = (res as Record<string, unknown>)?.data ?? res;
  if (typeof d !== 'object' || d === null) return null;
  const inner = (d as Record<string, unknown>)?.data ?? d;
  if (typeof inner !== 'object' || inner === null) return null;
  return inner as PageData;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
const PagePreview: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);

    // Try admin instances list endpoint — supports drafts
    api
      .get('/admin/page-templates/components/instances/list')
      .then((res: unknown) => {
        const list = Array.isArray(res)
          ? res
          : Array.isArray((res as Record<string, unknown>)?.data)
          ? (res as Record<string, unknown[]>).data
          : Array.isArray(((res as Record<string, unknown>)?.data as Record<string, unknown>)?.data)
          ? (((res as Record<string, unknown>)?.data as Record<string, unknown[]>).data)
          : [];

        const found = (list as PageData[]).find(
          (p) => p.slug === slug || p.slug === `/${slug}`
        );

        if (found) {
          setPage(found);
        } else {
          // Fallback: try public endpoint
          return api.get(`/site-pages/${encodeURIComponent(slug)}`);
        }
      })
      .then((res: unknown) => {
        if (res && !page) {
          const found = unwrapPage(res);
          if (found) setPage(found);
          else setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Loading ──────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  /* ── Not found ────────────────────────────────────────────────── */
  if (notFound || !page) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-center px-6">
        <h1 className="text-4xl font-bold mb-2">404</h1>
        <p className="text-muted-foreground mb-6">
          No page found with slug "{slug}".
        </p>
        <Link to="/admin/page-builder" className="text-primary hover:underline text-sm">
          ← Back to Page Builder
        </Link>
      </div>
    );
  }

  /* ── Determine render mode ────────────────────────────────────── */
  const sc = page.section_content ?? { blocks: [], settings: {} };
  const siteTemplate = sc.settings?.siteTemplate;
  const hasBlocks = (sc.blocks ?? []).length > 0;

  // Template-based pages (e.g. landing, hivefund) use SiteTemplateRenderer
  const useTemplateRenderer = siteTemplate && !hasBlocks;

  const isDraft = page.status !== 'published';

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-background">
      {/* Preview banner */}
      {isDraft && (
        <div className="sticky top-0 z-50 bg-amber-400 text-amber-950 px-4 py-2 flex items-center justify-between text-sm font-medium shadow-md">
          <div className="flex items-center gap-2">
            <AlertTriangle size={15} />
            <span>Preview Mode — this page is a draft and not publicly visible</span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/page-builder"
              className="flex items-center gap-1 hover:underline text-xs"
            >
              <ArrowLeft size={13} /> Page Builder
            </Link>
            <a
              href={`/p/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:underline text-xs"
            >
              <ExternalLink size={13} /> Public URL
            </a>
          </div>
        </div>
      )}

      {/* Published banner (lighter) */}
      {!isDraft && (
        <div className="bg-green-50 border-b border-green-200 text-green-800 px-4 py-1.5 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <Eye size={13} />
            <span>Preview — this page is published at{' '}
              <a href={`/p/${slug}`} className="underline font-medium">/p/{slug}</a>
            </span>
          </div>
          <Link to="/admin/page-builder" className="hover:underline flex items-center gap-1">
            <ArrowLeft size={12} /> Builder
          </Link>
        </div>
      )}

      {/* Page content */}
      {useTemplateRenderer ? (
        <SiteTemplateRenderer
          templateId={siteTemplate}
          sections={(sc.settings?.sections ?? {}) as Record<string, Record<string, unknown>>}
          isPreview
        />
      ) : (
        <Suspense
          fallback={
            <div className="min-h-[50vh] flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          }
        >
          <PageRenderer
            config={{
              blocks: sc.blocks ?? [],
              settings: sc.settings ?? {},
            }}
            isEditing={false}
          />
        </Suspense>
      )}
    </div>
  );
};

export default PagePreview;
